import React, { useMemo } from 'react';
import { Accordion, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { isArray } from 'lodash';
import { useSelector } from 'react-redux';

import { FormGroupControl } from '@components/Base';
import { getBMI } from '@utils/health';
import { GenderFormGroupSelect } from '@components/Feature/User';
import { FormValues, MemberFormValues } from './type';
import { selectIsCustomer } from '@components/Auth';
import { useIsDisabled } from '@hooks/formik';

const GROUP_PROPS = { className: 'mb-3' };

const getMemberValue = (values: FormValues, name: string) => {
  const [key, idx] = name.split('.');
  const value = values[key as keyof FormValues];
  return isArray(value) ? value[Number(idx)] : value as MemberFormValues;
}

type Props = {
  name: string;
  label: string;
};

const ContactFormControl = ({ name, label }: Props) => {
  const { t } = useTranslation();
  const isCustomer = useSelector(selectIsCustomer);
  const { initialValues, values, setFieldValue } = useFormikContext<FormValues>();
  const memberInitialValues = useMemo(() => getMemberValue(initialValues, name), [initialValues, name]);
  const memberValues = useMemo(() => getMemberValue(values, name), [values, name]);
  const disabled = useIsDisabled(memberInitialValues, isCustomer, {
    dob: true,
    gender: true,
    bmi: true,
  });

  const handleBlur = () => {
    const { weight, height } = memberValues;
    if (weight && height) {
      setFieldValue(`${name}.bmi`, getBMI(weight, height), true);
    }
  };

  return (
    <Accordion.Item eventKey={name} key={name}>
      <Accordion.Header>
        <span className="fw-bold">{label}</span>
      </Accordion.Header>
      <Accordion.Body className="d-grid gap-3">
        <FormGroupControl
          required
          name={`${name}.name`}
          label={t('user.name')}
          groupProps={GROUP_PROPS}
          disabled={disabled.name}
        />

        <FormGroupControl
          name={`${name}.nameKm`}
          label={t('user.nameKm')}
          groupProps={GROUP_PROPS}
          disabled={disabled.nameKm}
        />

        <Row>
          <Col>
            <GenderFormGroupSelect
              required
              name={`${name}.gender`}
              groupProps={GROUP_PROPS}
              disabled={disabled.gender}
            />
          </Col>

          <Col>
            <FormGroupControl
              required
              type="date"
              name={`${name}.dob`}
              label={t('user.dob')}
              groupProps={GROUP_PROPS}
              disabled={disabled.dob}
            />
          </Col>
        </Row>

        <Row xs={{ cols: 3 }}>
          <Col>
            <FormGroupControl
              type="number"
              name={`${name}.weight`}
              label={t('user.weight')}
              groupProps={GROUP_PROPS}
              onBlur={handleBlur}
              required
              disabled={disabled.weight}
            />
          </Col>

          <Col>
            <FormGroupControl
              type="number"
              name={`${name}.height`}
              label={t('user.height')}
              groupProps={GROUP_PROPS}
              onBlur={handleBlur}
              required
              disabled={disabled.height}
            />
          </Col>

          <Col>
            <FormGroupControl
              type="number"
              name={`${name}.bmi`}
              label={t('user.bmi')}
              groupProps={GROUP_PROPS}
              required
              disabled={disabled.bmi}
            />
          </Col>
        </Row>

        <FormGroupControl
          name={`${name}.personalCode`}
          label={t('user.personalCode')}
          groupProps={GROUP_PROPS}
          disabled={disabled.personalCode}
        />

        <FormGroupControl
          required
          name={`${name}.occupation`}
          label={t('user.occupation')}
          groupProps={GROUP_PROPS}
          disabled={disabled.occupation}
        />
        <FormGroupControl
          name={`${name}.email`}
          label={t('user.email')}
          groupProps={GROUP_PROPS}
          disabled={disabled.email}
        />
        <FormGroupControl
          required
          name={`${name}.mobileNumber`}
          label={t('user.mobileNumber')}
          groupProps={GROUP_PROPS}
          disabled={disabled.mobileNumber}
        />
        <FormGroupControl
          required
          name={`${name}.address`}
          label={t('user.address')}
          groupProps={GROUP_PROPS}
          disabled={disabled.address}
        />
      </Accordion.Body>
    </Accordion.Item>
  );
};

export { ContactFormControl };
